import React, { useState } from 'react';
import { TreeView, type TreeNode } from '@/components/ui';
import { useNotificationStore } from '@/stores/notificationStore';
import { Globe, Settings, Users, FileText, Image, Code, Database, Lock } from 'lucide-react';
import { CodeBlock } from '../CodeBlock';
import { SectionWrapper } from '../SectionWrapper';

// ─── Sample data (compact) ────────────────────────────────────────

const fileSystemNodes: TreeNode[] = [
  {
    id: 'src',
    label: 'src',
    children: [
      {
        id: 'components',
        label: 'components',
        children: [
          { id: 'button', label: 'Button.tsx' },
          { id: 'input', label: 'Input.tsx' },
        ],
      },
      { id: 'app', label: 'App.tsx' },
      { id: 'main', label: 'main.tsx' },
    ],
  },
  { id: 'package', label: 'package.json' },
  { id: 'tsconfig', label: 'tsconfig.json' },
];

const customIconNodes: TreeNode[] = [
  {
    id: 'app-settings',
    label: 'Application',
    icon: <Globe className="w-4 h-4 text-blue-500" />,
    children: [
      { id: 'general', label: 'General Settings', icon: <Settings className="w-4 h-4 text-muted-foreground" /> },
      { id: 'users', label: 'User Management', icon: <Users className="w-4 h-4 text-green-500" /> },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    icon: <FileText className="w-4 h-4 text-purple-500" />,
    children: [
      { id: 'docs', label: 'Documents', icon: <FileText className="w-4 h-4 text-purple-400" /> },
      { id: 'media', label: 'Media Library', icon: <Image className="w-4 h-4 text-pink-500" /> },
    ],
  },
];

// ─── Section Component ────────────────────────────────────────────

export function TreeViewSection() {
  const notifySuccess = useNotificationStore(s => s.success);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  return (
    <SectionWrapper title="TreeView Component">
        {/* Basic Tree + Custom Icons side by side */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Basic & Custom Icons</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Default (folder/file icons)</p>
              <TreeView nodes={fileSystemNodes} defaultExpandAll />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">Custom icons per node</p>
              <TreeView nodes={customIconNodes} defaultExpandAll showDefaultIcons={false} />
            </div>
          </div>
          <CodeBlock>
{`import { TreeView, type TreeNode } from '@/components/ui';

const nodes: TreeNode[] = [
  {
    id: 'src',
    label: 'src',
    children: [
      { id: 'app', label: 'App.tsx' },
      { id: 'main', label: 'main.tsx' },
    ],
  },
  { id: 'package', label: 'package.json' },
];

// Default icons
<TreeView nodes={nodes} />

// Expanded by default
<TreeView nodes={nodes} defaultExpandAll />

// Custom icons (set showDefaultIcons={false})
import { Globe, Settings } from 'lucide-react';

const customNodes: TreeNode[] = [
  {
    id: 'app',
    label: 'Application',
    icon: <Globe className="w-4 h-4 text-blue-500" />,
    children: [
      { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
    ],
  },
];

<TreeView nodes={customNodes} showDefaultIcons={false} />`}
          </CodeBlock>
        </div>

        {/* Sizes & Variants in a compact grid */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Sizes & Variants</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Small</p>
              <TreeView nodes={fileSystemNodes} size="sm" defaultExpandAll />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">Bordered</p>
              <TreeView nodes={fileSystemNodes} variant="bordered" defaultExpandAll />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">Filled</p>
              <TreeView nodes={fileSystemNodes} variant="filled" defaultExpandAll />
            </div>
          </div>
          <CodeBlock>
{`// Sizes: sm | md (default) | lg
<TreeView nodes={nodes} size="sm" />
<TreeView nodes={nodes} size="lg" />

// Variants: default | bordered | filled
<TreeView nodes={nodes} variant="default" />
<TreeView nodes={nodes} variant="bordered" />
<TreeView nodes={nodes} variant="filled" />`}
          </CodeBlock>
        </div>

        {/* Selection modes side by side */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Selection & Disabled Nodes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Single select (click a node)</p>
              <TreeView
                nodes={fileSystemNodes}
                defaultExpandAll
                selectionMode="single"
                selectedIds={selected}
                onSelect={(ids, node) => {
                  setSelected(ids);
                  notifySuccess(`Selected: ${node.label}`);
                }}
              />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">Disabled nodes</p>
              <TreeView
                nodes={[
                  {
                    id: 'root', label: 'Project', children: [
                      { id: 'open', label: 'Accessible File' },
                      { id: 'locked', label: 'Locked File', disabled: true },
                      { id: 'also-open', label: 'Another File' },
                    ],
                  },
                ]}
                defaultExpandAll
                selectionMode="single"
              />
            </div>
          </div>
          <CodeBlock>
{`// Single selection
const [selected, setSelected] = useState<Set<string>>(new Set());

<TreeView
  nodes={nodes}
  selectionMode="single"
  selectedIds={selected}
  onSelect={(ids, node) => setSelected(ids)}
/>

// Multiple selection (shows checkboxes)
<TreeView
  nodes={nodes}
  selectionMode="multiple"
  selectedIds={selected}
  onSelect={(ids, node) => setSelected(ids)}
/>

// Disabled nodes
const nodes: TreeNode[] = [
  { id: 'open', label: 'Accessible File' },
  { id: 'locked', label: 'Locked File', disabled: true },
];`}
          </CodeBlock>
        </div>
    </SectionWrapper>
  );
}
